// SPDX-License-Identifier: AGPL-3.0-or-later

import {useNSFWMedia} from '@app/features/messaging/hooks/useNSFWMedia';
import styles from '@app/features/theme/styles/NSFWBlur.module.css';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type {ReactElement} from 'react';
import React, {useCallback} from 'react';

interface NSFWEmojiWrapperProps {
	nsfw: boolean;
	channelId: string | undefined;
	children: ReactElement<{className?: string}>;
}
export const NSFWEmojiWrapper = observer(({nsfw, channelId, children}: NSFWEmojiWrapperProps): ReactElement | null => {
	const {t} = useLingui();
	const {shouldBlur, shouldBlock, canReveal, reveal} = useNSFWMedia(nsfw, channelId);
	const handleReveal = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			if (shouldBlur && canReveal) {
				e.preventDefault();
				e.stopPropagation();
				reveal();
			}
		},
		[shouldBlur, canReveal, reveal],
	);
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') {
				return;
			}
			handleReveal(e);
		},
		[handleReveal],
	);
	if (shouldBlock) {
		return null;
	}
	if (shouldBlur) {
		if (!canReveal) {
			return (
				<span className={styles.nsfwBlurContainer}>
					{React.cloneElement(children, {
						className: clsx(children.props.className, styles.nsfwBlurred),
					})}
				</span>
			);
		}
		return (
			<span
				className={styles.nsfwBlurContainer}
				onClick={handleReveal}
				onKeyDown={handleKeyDown}
				role="button"
				tabIndex={0}
				aria-label={t`Reveal sensitive emoji`}
			>
				{React.cloneElement(children, {
					className: clsx(children.props.className, styles.nsfwBlurred),
				})}
			</span>
		);
	}
	return children;
});
